import { Box, Dialog, Stack } from "@mui/material";
import * as Sentry from "@sentry/react";
import { cloneDeep, isEqual, keys, pick } from "lodash";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "urql";

import { UploadFileAttachmentInput } from "@/components/graphql/schema.graphql";
import { useFilePickerState } from "@/components/ui-v2/FilePicker/state";
import { FilepickerFile } from "@/components/ui-v2/FilePicker/types";
import {
  ReUploadFile,
  UploadedFileFragment,
  UploadFile,
} from "@/features/drive/queries.graphql";
import {
  useQueryWithResponseHandler,
  useShowSnackbar,
  useUrqlResponseHandler,
} from "@/hooks";
import { dataURLtoBlob } from "@/utils/blobs";
import { uploadFileToUrl } from "@/utils/upload";

import { GetWorkspacePoseCurrentFile } from "../../queries.graphql";
import { usePoserStore } from "../../state";
import { PoseData, PoseViewData } from "../../types";
import {
  glbAttachmentName,
  incrementCacheVersionsFromFile,
  poseDataFromFile,
  posePresets,
  viewPresets,
} from "../../utils";
import { CharacterHandle, Mannequin } from "./components/Mannequin/Mannequin";
import { Sidebar } from "./components/Sidebar/Sidebar";

interface PoserProps {
  workspaceId: string;
}

const getInitialState = (): PoseData => ({
  ...cloneDeep(viewPresets.fullShot.data),
  ...cloneDeep(posePresets.tPose.data),
  name: "",
  fileId: undefined,
  characterVariant: "mannequin",
  isDirty: false,
  isPoseDirty: false,
  isSaving: false,
  isSaved: false,
  modelLoadedAt: null,
  modelLoadVersion: 0,
  urlCacheVersions: {},
});

export const PoseCreator = (props: PoserProps) => {
  const { workspaceId } = props;

  const characterRef = useRef<CharacterHandle>(null);
  const {
    isPoseCreatorOpen,
    closePoser,
    closePoseCreator,
    referencePoseData,
    referencePoseFileId,
    selectTab,
  } = usePoserStore();

  const { setSelectedFile, setSelectionAsConfirmed } = useFilePickerState();

  const [poseData, setPoseData] = useState<PoseData>(getInitialState());
  const { showSuccess, showError } = useShowSnackbar();
  const { getResponseFromState } = useUrqlResponseHandler();
  const {
    data: { currentFile },
  } = useQueryWithResponseHandler(
    {
      query: GetWorkspacePoseCurrentFile,
      variables: {
        fileId: referencePoseFileId,
        fetchFile: !!referencePoseFileId,
      },
    },
    {
      showSnackbarOnError: true,
    },
  );
  const [, uploadFile] = useMutation(UploadFile);
  const [, reuploadFile] = useMutation(ReUploadFile);

  useEffect(() => {
    if (isPoseCreatorOpen) {
      setPoseData((prevState) => ({
        ...prevState,
        modelLoadedAt: Date.now(),
      }));
    }
  }, [isPoseCreatorOpen]);

  useEffect(() => {
    // Handle edit scenario
    if (currentFile) {
      setPoseData((prevPoseData) => ({
        ...prevPoseData,
        ...currentFile.metadata?.poseData,
        name: currentFile.name.split(".")[0],
        modelUrl:
          currentFile.attachmentUrls?.[glbAttachmentName] ??
          prevPoseData.modelUrl,
      }));
    }
  }, [currentFile]);

  useEffect(() => {
    // Handle creating with library pose as reference
    if (referencePoseData) {
      setPoseData((prevPoseData) => ({
        ...prevPoseData,
        ...referencePoseData.viewData,
        modelUrl: referencePoseData.modelUrl,
      }));
    }
  }, [referencePoseData]);

  useEffect(() => {
    // Handle creating with library pose
    if (referencePoseFileId) {
      setPoseData((prevPoseData) => ({
        ...prevPoseData,
        fileId: referencePoseFileId,
      }));
    }
  }, [referencePoseFileId]);

  const handleReset = () => {
    setPoseData(getInitialState());
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // To prevent FilePicker from closing
    if (event.key === "Enter") {
      event.stopPropagation();
    }
  };

  // export and save
  const handleUsePose = useCallback(async () => {
    let imageUrl: string;
    if (poseData.imageUrl && !poseData.isDirty) {
      imageUrl = poseData.imageUrl;
    } else if (characterRef.current) {
      imageUrl = await characterRef.current.exportPoseImage();
    } else {
      return;
    }

    const file: FilepickerFile = {
      id: poseData.fileId ?? "",
      src: imageUrl,
      name: poseData.name || "Pose",
      type: "pose",
      contentType: "image/png",
      thumbnailSrc: imageUrl,
      interactedAt: new Date(),
      source: "pose",
    };

    setSelectedFile(file);
    setSelectionAsConfirmed();

    closePoser();
  }, [poseData]);

  const onPoseChange = useCallback(() => {
    setPoseData((prev) => ({ ...prev, isDirty: true, isPoseDirty: true }));
  }, [setPoseData]);

  const handleSavePose = useCallback(async () => {
    if (!characterRef.current) return;
    setPoseData((prev) => ({ ...prev, isSaving: true }));
    try {
      const poseImageBlob = dataURLtoBlob(
        await characterRef.current.exportPoseImage(),
      );
      const poseModelBlob = new Blob(
        [await characterRef.current.exportPoseModel()],
        { type: "model/gltf-binary" },
      );
      const attachmentsInput: UploadFileAttachmentInput[] = [];
      if (!poseData.fileId || poseData.isPoseDirty) {
        attachmentsInput.push({
          name: glbAttachmentName,
          contentType: "model/gltf-binary",
          sizeBytes: poseModelBlob.size,
        });
      }
      const imageFileName = `${poseData.name}.png`;
      const metadata = {
        poseData: pick(poseData, ["camera", "orbitControl"]),
      };

      let savedFile: UploadedFileFragment | null = null;
      if (poseData.fileId) {
        const result = await reuploadFile({
          updateFileInput: {
            fileId: poseData.fileId,
            name: imageFileName,
            metadata,
            reupload: true,
            sizeBytes: poseImageBlob.size,
            attachments: attachmentsInput,
          },
        });
        const {
          data: { updateFile },
          error,
        } = getResponseFromState({ state: result });
        if (updateFile) {
          savedFile = updateFile;
        } else {
          showError("Failed to save pose. Please try again.");
          Sentry.captureMessage("Failed to save pose.", {
            extra: { error },
          });
          return;
        }
      } else {
        const result = await uploadFile({
          uploadFileInput: {
            workspaceId,
            name: imageFileName,
            contentType: "image/png",
            sizeBytes: poseImageBlob.size,
            sourceType: "POSE",
            metadata,
            attachments: attachmentsInput,
          },
        });
        const {
          data: { uploadFile: uploadedFile },
          error,
        } = getResponseFromState({ state: result });
        if (uploadedFile) {
          savedFile = uploadedFile;
        } else {
          showError("Failed to save pose. Please try again.");
          Sentry.captureMessage("Failed to save pose.", {
            extra: { error },
          });
          return;
        }
      }

      if (!savedFile.uploadUrl) {
        showError("Failed to save pose. Please try again.");
        Sentry.captureMessage("Failed to save pose, no upload URL");
        return;
      }

      const uploads: Promise<{ url: string; size: number } | void>[] = [
        uploadFileToUrl({
          signedUrl: savedFile.uploadUrl,
          contentType: savedFile.contentType,
          blob: poseImageBlob,
        }),
      ];
      attachmentsInput.forEach((attachment) => {
        const uploadAttachment = async () => {
          let file: Blob | undefined = undefined;
          if (attachment.name === glbAttachmentName) {
            file = poseModelBlob;
          }
          if (!file || !savedFile.attachmentUploadUrls?.[attachment.name])
            return;
          return await uploadFileToUrl({
            signedUrl: savedFile.attachmentUploadUrls[attachment.name],
            contentType: attachment.contentType,
            blob: file,
          });
        };
        uploads.push(uploadAttachment());
      });
      await Promise.all(uploads);

      setPoseData((prev) => ({
        ...poseDataFromFile(savedFile, prev),
        urlCacheVersions: incrementCacheVersionsFromFile(savedFile, prev),
        isDirty: false,
        isPoseDirty: false,
        isSaved: true,
      }));
      showSuccess("Pose successfully saved to workspace poses");
      selectTab("workspace");

      setTimeout(() => {
        setPoseData((prev) => ({ ...prev, isSaved: false }));
      }, 2000);
    } finally {
      setPoseData((prev) => ({
        ...prev,
        isSaving: false,
        modelLoadedAt: Date.now(),
      }));
    }
  }, [setPoseData, poseData]);

  const onViewChange = useCallback(
    (viewData: PoseViewData) => {
      setPoseData((prev) => {
        const isDirty = !isEqual(viewData, pick(prev, keys(viewData)));
        if (!isDirty) {
          return prev;
        }
        return {
          ...prev,
          ...viewData,
          isDirty,
        };
      });
    },
    [setPoseData],
  );

  return (
    <Dialog
      open={isPoseCreatorOpen}
      onKeyDown={handleKeyDown}
      onClose={closePoseCreator}
      onTransitionExited={handleReset}
      hideBackdrop
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "100%",
        },
      }}
    >
      <Stack
        direction="row"
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <Stack
          direction="row"
          style={{
            width: "100%",
            height: "100%",
            justifyContent: "center",
            alignItems: "flex-start",
            background: "black",
            position: "relative",
          }}
        >
          <Suspense fallback={null}>
            <Mannequin
              ref={characterRef}
              poseData={poseData}
              setPoseData={setPoseData}
              onPoseChange={onPoseChange}
              onViewChange={onViewChange}
            />
          </Suspense>
        </Stack>
        <Stack
          direction="column"
          style={{
            width: "400px",
          }}
          sx={{
            padding: 4,
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Sidebar
              poseData={poseData}
              setPoseData={setPoseData}
              onUsePose={handleUsePose}
              onSavePose={handleSavePose}
            />
          </Box>
        </Stack>
      </Stack>
    </Dialog>
  );
};
